import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://formspree.io/f/mvzbjbee', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* CTA Banner */}
      <section className="relative py-20 bg-[#080810] overflow-hidden">
        {/* Violet glow background */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[800px] h-[400px] bg-[#7B3FE4]/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-8 text-center">
          <h2 
            className="text-4xl sm:text-6xl font-bold text-[#F0EFF8] mb-6 leading-tight"
            style={{ fontFamily: 'Sora, sans-serif', letterSpacing: '-0.02em' }}
          >
            ¿Listo para dejar de operar<br />en modo manual?
          </h2>
          
          <p
            className="text-[#8887A0] text-xl mb-10 max-w-[700px] mx-auto"
            style={{ fontFamily: 'Sora, sans-serif', lineHeight: '1.6' }}
          >
            No importa el tamaño de tu negocio ni la industria. Si tienes procesos que se repiten, tenemos una solución.
          </p>
          
          <button
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="px-8 py-4 bg-[#7B3FE4] text-white text-base hover:bg-[#6B2FD4] transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            style={{ 
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              borderRadius: '50px',
              boxShadow: '0 0 24px rgba(123,63,228,0.6)'
            }}
          >
            Agendar Diagnóstico Gratis
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="relative py-20 bg-[#0D0D1A] overflow-hidden">
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8">
          {/* Centered Header */}
          <div className="flex flex-col items-center text-center mb-14">
            {/* Section Label */}
            <div 
              className="text-[#9B6FE4] uppercase font-medium flex items-center gap-3 mb-8"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', letterSpacing: '0.2em' }}
            >
              <div className="w-6 h-[2px] bg-[#7B3FE4]" />
              Contacto
              <div className="w-6 h-[2px] bg-[#7B3FE4]" />
            </div>

            <h3
              className="text-5xl font-bold text-[#F0EFF8] leading-tight mb-6"
              style={{ fontFamily: 'Sora, sans-serif', letterSpacing: '-0.02em' }}
            >
              Hablemos de tu negocio
            </h3>

            <p
              className="text-[#8887A0] max-w-[600px] mb-4"
              style={{ fontFamily: 'Sora, sans-serif', fontSize: '17px', lineHeight: '1.7' }}
            >
              Cuéntanos qué procesos te están quitando tiempo. Te respondemos en menos de 24 horas con una evaluación inicial sin costo.
            </p>

            <a
              href="mailto:hola@intevolabs.com"
              className="text-[#7B3FE4] text-lg font-semibold hover:text-[#9B5FFF] transition-colors duration-200 inline-block cursor-pointer"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              hola@intevolabs.com
            </a>
          </div>

          {/* Centered Form */}
          <div className="flex justify-center">
            <div className="w-full max-w-[600px]">
              <form 
                id="contact-form"
                name="contact"
                method="POST"
                data-netlify="true"
                onSubmit={handleSubmit} 
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                {/* Name Input */}
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre"
                    required
                    className="w-full h-14 bg-transparent border border-white/[0.06] px-5 text-[#F0EFF8] placeholder-[#8887A0] focus:outline-none focus:border-[#7B3FE4] transition-colors duration-200"
                    style={{ 
                      fontFamily: 'Sora, sans-serif',
                      fontSize: '16px',
                      borderRadius: '2px'
                    }}
                  />
                </div>

                {/* Email Input */}
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Email"
                    required
                    className="w-full h-14 bg-transparent border border-white/[0.06] px-5 text-[#F0EFF8] placeholder-[#8887A0] focus:outline-none focus:border-[#7B3FE4] transition-colors duration-200"
                    style={{ 
                      fontFamily: 'Sora, sans-serif',
                      fontSize: '16px',
                      borderRadius: '2px'
                    }}
                  />
                </div>

                {/* Company Input */}
                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Empresa"
                    required
                    className="w-full h-14 bg-transparent border border-white/[0.06] px-5 text-[#F0EFF8] placeholder-[#8887A0] focus:outline-none focus:border-[#7B3FE4] transition-colors duration-200"
                    style={{ 
                      fontFamily: 'Sora, sans-serif',
                      fontSize: '16px',
                      borderRadius: '2px'
                    }}
                  />
                </div>

                {/* Message Textarea */}
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    placeholder="¿Cuál es el proceso que más tiempo te quita hoy?"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full bg-transparent border border-white/[0.06] px-5 py-4 text-[#F0EFF8] placeholder-[#8887A0] focus:outline-none focus:border-[#7B3FE4] transition-colors duration-200 resize-none"
                    style={{ 
                      fontFamily: 'Sora, sans-serif',
                      fontSize: '16px',
                      borderRadius: '2px'
                    }}
                  />
                  <div className="text-right text-xs text-[#8887A0] mt-2" style={{ fontFamily: 'monospace' }}>
                    {formData.message.length}/500
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-[#7B3FE4] text-white text-sm hover:bg-[#6B2FD4] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  style={{ 
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                    borderRadius: '50px',
                    boxShadow: '0 0 24px rgba(123,63,228,0.6)'
                  }}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar mensaje'}
                  {!isSubmitting && <i className="ri-arrow-right-line text-lg"></i>}
                </button>

                {/* Trust line */}
                <div className="flex items-center justify-center gap-2 text-[#8887A0]" style={{ fontFamily: 'Sora, sans-serif', fontSize: '14px' }}>
                  <i className="ri-check-line text-[#7B3FE4] text-base"></i>
                  Al enviar, nuestro AI Lab analizará tu caso y te contactaremos en menos de 24 horas con una ruta de automatización. Sin compromiso.
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="text-center text-[#7B3FE4] text-sm font-medium">
                    <i className="ri-check-line text-lg mr-2"></i>
                    Mensaje enviado. Te contactaremos pronto.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="text-center text-red-400 text-sm font-medium">
                    <i className="ri-error-warning-line text-lg mr-2"></i>
                    Error al enviar. Intenta de nuevo.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}